/**
 * Admin Announcements - Send announcements to users
 */
import { useLanguage } from "@/context/AppContext";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, Bell, Send, Loader2, Calendar, Users
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAnnouncements = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    target: 'all',
    target_email: ''
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`, { withCredentials: true });
      setAnnouncements(res.data.announcements || []);
    } catch (error) {
      console.error('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const sendAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.message.trim()) {
      toast.error('Titel und Nachricht erforderlich');
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/admin/announcement`, newAnnouncement, { withCredentials: true });
      toast.success('Ankündigung gesendet!');
      setNewAnnouncement({ title: '', message: '', target: 'all', target_email: '' });
      fetchAnnouncements();
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const getTargetLabel = (target) => {
    switch (target) {
      case 'all': return 'Alle Benutzer';
      case 'clients': return 'Nur Kunden';
      case 'providers': return 'Nur Dienstleister';
      case 'email': return 'Spezifische E-Mail';
      default: return target;
    }
  };

  return (
    <div className="min-h-screen infirmly-dark bg-gray-100" style={{ background: "radial-gradient(ellipse at top, #0F2557 0%, #0A1A3E 60%, #050B23 100%)" }} data-testid="admin-announcements">
      {/* Header */}
      <header className="border-b border-white/10 sticky bg-white/[0.04] backdrop-blur top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="rounded-full">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('adminPageAnnouncements')}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* New Announcement */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Neue Ankündigung
            </CardTitle>
            <CardDescription>
              Senden Sie eine Push-Benachrichtigung an alle Benutzer
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Titel</Label>
              <Input
                id="title"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                placeholder="Ankündigungstitel..."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="message">Nachricht</Label>
              <Textarea
                id="message"
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                placeholder="Ihre Nachricht an die Benutzer..."
                rows={4}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Zielgruppe</Label>
              <Select 
                value={newAnnouncement.target} 
                onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, target: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Benutzer</SelectItem>
                  <SelectItem value="clients">Nur Kunden</SelectItem>
                  <SelectItem value="providers">Nur Dienstleister</SelectItem>
                  <SelectItem value="email">Spezifische E-Mail</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newAnnouncement.target === 'email' && (
              <div>
                <Label>E-Mail-Adresse</Label>
                <Input
                  value={newAnnouncement.target_email}
                  onChange={e => setNewAnnouncement(p => ({ ...p, target_email: e.target.value }))}
                  placeholder="nutzer@beispiel.ch"
                  className="mt-1"
                  data-testid="announcement-target-email"
                />
              </div>
            )}
            
            <Button 
              onClick={sendAnnouncement} 
              disabled={sending}
              className="bg-[#0044CC] hover:bg-[#003399] rounded-full"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Senden...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Ankündigung senden</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Announcement History */}
        <Card>
          <CardHeader>
            <CardTitle>Verlauf</CardTitle>
            <CardDescription>
              Frühere Ankündigungen
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#0044CC]" />
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Noch keine Ankündigungen gesendet</p>
              </div>
            ) : (
              <div className="divide-y">
                {announcements.map((announcement) => (
                  <div key={announcement.announcement_id} className="py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{announcement.title}</p>
                        <p className="text-sm text-gray-600 mt-1">{announcement.message}</p>
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p className="flex items-center gap-1 justify-end">
                          <Calendar className="w-3 h-3" />
                          {new Date(announcement.created_at).toLocaleDateString('de-CH')}
                        </p>
                        <p className="flex items-center gap-1 justify-end mt-1">
                          <Users className="w-3 h-3" />
                          {getTargetLabel(announcement.target || 'all')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminAnnouncements;
